import { gameAPI } from '../services/gameAPI';
import { useGameStore } from '../store/gameStore';
import { toast } from '../hooks/use-toast';

/**
 * Restart a featured case in place.
 *
 * The compat restart route returns a fresh `load` payload, so the player stays
 * on the game screen with the same case replayed from turn 0 — no client-only
 * wipe and no bounce to the menu (a generated case has no server-side restart,
 * which is why that path still resets locally).
 *
 * @param {string} sessionId - a `featured:{slug}` session id.
 * @returns {Promise<boolean>} whether the restart landed.
 */
export const restartFeaturedCase = async (sessionId) => {
  try {
    const savedData = await gameAPI.restartCase(sessionId);
    useGameStore.getState().loadSavedGame({
      theme: savedData.theme,
      difficulty: savedData.difficulty,
      caseId: savedData.caseId,
      sessionId: savedData.sessionId,
      gameState: savedData.gameState,
      messageHistory: savedData.messageHistory,
      guideCharacter: savedData.guideCharacter,
      caseData: savedData.caseData,
      lastImage: savedData.lastImage,
      lastImageVantage2: savedData.lastImageVantage2,
      // Same session and same case id as before, so the store cannot infer
      // that the playthrough started over; say it.
      isRestart: true,
    });
    toast({
      title: 'Case restarted',
      description: 'This featured case is back at the beginning.',
    });
    return true;
  } catch (error) {
    console.error('Failed to restart featured case:', error);
    toast({
      title: 'Restart failed',
      description: 'Could not restart this case. Please try again.',
      variant: 'destructive',
    });
    return false;
  }
};
